import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { CheckCircle2, XCircle, RefreshCw, Plug, CalendarClock, Upload } from '@/components/icons';
import { SectionHeader } from '@/components/ui/SectionHeader';
import { Button } from '@/components/ui/Button';
import { FormGroup } from '@/components/form/FormGroup';
import { Input } from '@/components/form/Input';
import { Select } from '@/components/form/Select';
import { useToast } from '@/components/ui/useToast';
import { useConfirm } from '@/components/ui/useConfirm';
import { apiError } from '@/lib/apiError';
import {
  getShopifyConfig, updateShopifyConfig, testShopifyConnection, syncShopifyCatalog,
  getShopifySkuMap, setShopifyOrderStartPoint, pushShopifyStock, getShopifyLogs, pushShopifyTracking,
  type ShopifyLocation, type CatalogSummary, type StockPushResult,
} from '@/api/shopify';

export function ShopifyConfigPage() {
  const qc = useQueryClient();
  const toast = useToast();
  const confirmDlg = useConfirm();
  const [form, setForm] = useState({ storeDomain: '', apiVersion: '2026-07', locationId: '', dryRun: true, safetyStock: 0, orderSyncEnabled: false, stockSyncEnabled: false, orderWriteback: false });
  const [locations, setLocations] = useState<ShopifyLocation[]>([]);
  const [catalog, setCatalog] = useState<CatalogSummary | null>(null);
  const [pushResult, setPushResult] = useState<StockPushResult | null>(null);

  const cfgQuery = useQuery({ queryKey: ['shopify-config'], queryFn: getShopifyConfig });
  const cfg = cfgQuery.data;
  useEffect(() => {
    if (!cfg) return;
    setForm({
      storeDomain: cfg.storeDomain ?? '', apiVersion: cfg.apiVersion ?? '2026-07', locationId: cfg.locationId ?? '',
      dryRun: cfg.dryRun, safetyStock: cfg.safetyStock, orderSyncEnabled: cfg.orderSyncEnabled, stockSyncEnabled: cfg.stockSyncEnabled, orderWriteback: cfg.orderWriteback,
    });
  }, [cfg]);

  const skuQuery = useQuery({ queryKey: ['shopify-skumap'], queryFn: () => getShopifySkuMap() });

  const save = useMutation({
    mutationFn: () => updateShopifyConfig(form),
    onSuccess: () => { toast.push('Shopify settings saved', 'success'); qc.invalidateQueries({ queryKey: ['shopify-config'] }); },
    onError: (e) => toast.push(apiError(e, 'Save failed'), 'error'),
  });
  const testConn = useMutation({
    mutationFn: testShopifyConnection,
    onSuccess: (r) => { setLocations(r.locations); toast.push(`Connected: ${r.shopName} (${r.domain})`, 'success'); },
    onError: (e) => toast.push(apiError(e, 'Connection failed'), 'error'),
  });
  const sync = useMutation({
    mutationFn: syncShopifyCatalog,
    onSuccess: (s) => { setCatalog(s); toast.push(`Catalog synced — ${s.mapped}/${s.mappable} SKUs mapped`, 'success'); qc.invalidateQueries({ queryKey: ['shopify-skumap'] }); },
    onError: (e) => toast.push(apiError(e, 'Catalog sync failed'), 'error'),
  });
  const pushTracking = useMutation({
    mutationFn: pushShopifyTracking,
    onSuccess: (r) => r.off
      ? toast.push('Order write-back is off — turn it on and save first.', 'error')
      : toast.push(`Checked ${r.checked} AWB(s): ${r.pushed} sent, ${r.already} already on Shopify, ${r.skipped} skipped, ${r.failed} failed`, r.failed ? 'error' : 'success'),
    onError: (e) => toast.push(apiError(e, 'Sending AWBs failed'), 'error'),
  });
  const setStart = useMutation({
    mutationFn: (iso: string) => setShopifyOrderStartPoint(iso),
    onSuccess: (r) => { toast.push(`Orders will be received from ${new Date(r.orderSinceCursor).toLocaleString()}`, 'success'); qc.invalidateQueries({ queryKey: ['shopify-config'] }); },
    onError: (e) => toast.push(apiError(e, 'Failed to set start point'), 'error'),
  });

  // Recent stock-push runs (button + 10-min cron) — the proof that pushes happen.
  const logsQuery = useQuery({ queryKey: ['shopify-logs'], queryFn: getShopifyLogs, refetchInterval: 60_000 });
  const pushLogs = (logsQuery.data ?? []).filter((l) => l.job === 'stock-push').slice(0, 6);
  const pushNow = useMutation({
    mutationFn: pushShopifyStock,
    onSuccess: (r) => {
      setPushResult(r);
      qc.invalidateQueries({ queryKey: ['shopify-logs'] });
      qc.invalidateQueries({ queryKey: ['shopify-config'] });
      qc.invalidateQueries({ queryKey: ['shopify-stock-preview'] });
      toast.push(
        r.dryRun
          ? `Dry run — ${r.candidates} product(s) would be updated (nothing written to Shopify)`
          : `Stock pushed — ${r.pushed} updated${r.errors ? `, ${r.errors} failed` : ''}`,
        r.errors ? 'warning' : 'success',
      );
    },
    onError: (e) => toast.push(apiError(e, 'Stock push failed'), 'error'),
  });
  async function onPushNow() {
    // Live mode writes to the real store — confirm first.
    if (!cfg?.dryRun && !(await confirmDlg('Push stock to the LIVE Shopify store now? Every changed product will be updated on Grhamoy.'))) return;
    pushNow.mutate();
  }

  async function onSetStartToday() {
    const d = new Date(); d.setHours(0, 0, 0, 0);
    if (!await confirmDlg(`Start receiving Shopify orders created from ${d.toLocaleDateString()} onward? Older orders will not be backfilled.`)) return;
    setStart.mutate(d.toISOString());
  }

  return (
    <div>
      <SectionHeader title="Shopify Integration" />

      {/* Token status */}
      <div className="mb-4 flex items-center gap-2 rounded-card border border-[var(--border-color)] bg-[var(--card-bg)] px-4 py-3 text-sm">
        {cfg?.tokenSet ? (
          <><CheckCircle2 size={16} className="text-success" /> <span>Admin token is set in the server environment.</span></>
        ) : (
          <><XCircle size={16} className="text-danger" /> <span><strong>SHOPIFY_TOKEN is not set.</strong> Add it to <code>backend/.env</code> and restart the backend.</span></>
        )}
      </div>

      <div className="grid grid-cols-1 gap-4 lg:grid-cols-2">
        {/* Settings */}
        <div className="rounded-card border border-[var(--border-color)] bg-[var(--card-bg)] p-4">
          <div className="mb-3 text-sm font-extrabold">Connection & Settings</div>
          <div className="grid grid-cols-2 gap-3">
            <FormGroup label="Store Domain" className="col-span-2">
              <Input value={form.storeDomain} onChange={(e) => setForm((f) => ({ ...f, storeDomain: e.target.value }))} placeholder="grhamoyhome.myshopify.com" />
            </FormGroup>
            <FormGroup label="API Version">
              <Input value={form.apiVersion} onChange={(e) => setForm((f) => ({ ...f, apiVersion: e.target.value }))} placeholder="2026-07" />
            </FormGroup>
            <FormGroup label="Location">
              {locations.length ? (
                <Select value={form.locationId} onChange={(e) => setForm((f) => ({ ...f, locationId: e.target.value }))}>
                  <option value="">— select —</option>
                  {locations.map((l) => <option key={l.id} value={l.id}>{l.name}{l.isActive ? '' : ' (inactive)'}</option>)}
                </Select>
              ) : (
                <Input value={form.locationId} onChange={(e) => setForm((f) => ({ ...f, locationId: e.target.value }))} placeholder="Test connection to list locations" />
              )}
            </FormGroup>
            <FormGroup label="Safety Stock">
              <Input type="number" min={0} value={String(form.safetyStock)} onChange={(e) => setForm((f) => ({ ...f, safetyStock: Number(e.target.value) || 0 }))} />
            </FormGroup>
            <FormGroup label="Dry Run (no Shopify writes)">
              <Select value={form.dryRun ? '1' : '0'} onChange={(e) => setForm((f) => ({ ...f, dryRun: e.target.value === '1' }))}>
                <option value="1">On — safe (log only)</option>
                <option value="0">Off — write to Shopify</option>
              </Select>
            </FormGroup>
            <FormGroup label="Order Sync">
              <Select value={form.orderSyncEnabled ? '1' : '0'} onChange={(e) => setForm((f) => ({ ...f, orderSyncEnabled: e.target.value === '1' }))}>
                <option value="0">Disabled</option>
                <option value="1">Enabled</option>
              </Select>
            </FormGroup>
            <FormGroup label="Stock Sync">
              <Select value={form.stockSyncEnabled ? '1' : '0'} onChange={(e) => setForm((f) => ({ ...f, stockSyncEnabled: e.target.value === '1' }))}>
                <option value="0">Disabled</option>
                <option value="1">Enabled</option>
              </Select>
            </FormGroup>
            <FormGroup label="Order write-back (tracking, cancel, notes)">
              <Select value={form.orderWriteback ? '1' : '0'} onChange={(e) => setForm((f) => ({ ...f, orderWriteback: e.target.value === '1' }))}>
                <option value="0">Off — log only</option>
                <option value="1">On — write to Shopify</option>
              </Select>
            </FormGroup>
          </div>
          <div className="mt-4 flex gap-2">
            <Button variant="primary" onClick={() => save.mutate()} disabled={save.isPending}>{save.isPending ? 'Saving…' : 'Save Settings'}</Button>
            <Button variant="glass" icon={<Plug size={14} />} onClick={() => testConn.mutate()} disabled={testConn.isPending}>{testConn.isPending ? 'Testing…' : 'Test Connection'}</Button>
          </div>

          <div className="mt-4 border-t border-[var(--border-color)] pt-3">
            <div className="mb-1 text-xs font-extrabold">Order pull start point</div>
            <p className="mb-2 text-xs text-[var(--text-muted)]">
              Only orders created after this point are pulled. Set it to today to skip the store's historical order backlog.
            </p>
            <div className="flex flex-wrap items-center gap-3">
              <Button variant="glass" size="sm" icon={<CalendarClock size={13} />} onClick={onSetStartToday} disabled={setStart.isPending}>
                {setStart.isPending ? 'Setting…' : 'Start from Today'}
              </Button>
              <span className="text-xs text-[var(--text-muted)]">
                Current: <strong>{cfg?.orderSinceCursor ? new Date(cfg.orderSinceCursor).toLocaleString() : 'not set (first pull starts now)'}</strong>
              </span>
            </div>
          </div>

          <div className="mt-4 border-t border-[var(--border-color)] pt-3">
            <div className="mb-1 text-xs font-extrabold">Delhivery tracking → Shopify</div>
            <p className="mb-2 text-xs text-[var(--text-muted)]">
              With Order write-back on, every new AWB marks the Shopify order fulfilled with its tracking link and Shopify emails the customer.
              Use this once to send the AWBs booked before it was switched on (parcels still in transit only — delivered ones are left alone).
            </p>
            <Button variant="glass" size="sm" icon={<Upload size={13} />} disabled={pushTracking.isPending || !cfg?.orderWriteback}
              onClick={async () => { if (await confirmDlg('Send existing AWBs to Shopify? Orders that already have their AWB on Shopify are skipped. Customers of newly fulfilled orders get Shopify’s shipping email.')) pushTracking.mutate(); }}>
              {pushTracking.isPending ? 'Sending…' : 'Send existing AWBs to Shopify'}
            </Button>
            {!cfg?.orderWriteback && <span className="ml-3 text-xs text-[var(--text-muted)]">Turn on Order write-back first.</span>}
          </div>
        </div>

        {/* Stock push — manual run of the same push the 10-min cron does */}
        <div className="rounded-card border border-[var(--border-color)] bg-[var(--card-bg)] p-4">
          <div className="mb-2 flex flex-wrap items-center justify-between gap-2">
            <div className="flex items-center gap-2 text-sm font-extrabold">
              Stock Push → Shopify
              <span className={`rounded-full px-2 py-0.5 text-[10px] font-bold ${cfg?.dryRun ? 'bg-[var(--bg-secondary)] text-[var(--text-muted)]' : 'bg-danger/10 text-danger'}`}>
                {cfg?.dryRun ? 'Dry run' : 'LIVE'}
              </span>
            </div>
            <Button variant="primary" size="sm" icon={<Upload size={13} />} onClick={onPushNow} disabled={pushNow.isPending || !cfg?.locationId}>
              {pushNow.isPending ? 'Pushing…' : 'Push stock now'}
            </Button>
          </div>
          <p className="mb-2 text-xs text-[var(--text-muted)]">
            Runs the same push as the automatic {cfg?.stockSyncEnabled ? 'every-10-minute' : '(currently off)'} sweep — never both at once.
            {cfg?.dryRun ? ' Dry run is ON: it only logs what would change; Shopify is not updated.' : ' LIVE: it updates stock on the Shopify store.'}
            {!cfg?.locationId && <strong className="text-danger"> Set a location first.</strong>}
          </p>
          <p className="mb-3 text-xs text-[var(--text-muted)]">
            Last live push: <strong>{cfg?.lastStockSyncAt ? new Date(cfg.lastStockSyncAt).toLocaleString() : 'never'}</strong>
            {' · '}<Link to="/admin/configurations/shopify-stock" className="font-semibold text-accent hover:underline">Open Ecommerce Inventory →</Link>
          </p>
          {pushResult && (
            <div className="mb-3 rounded-md border border-[var(--border-color)] bg-[var(--bg-secondary)] p-2.5 text-xs">
              <div className="mb-1 font-bold">
                {pushResult.dryRun
                  ? `Dry run: ${pushResult.candidates} product(s) would change — nothing written to Shopify.`
                  : `Pushed ${pushResult.pushed} of ${pushResult.candidates}${pushResult.errors ? ` · ${pushResult.errors} failed (see log)` : ''}.`}
              </div>
              {pushResult.sample.length > 0 && (
                <div className="text-[var(--text-muted)]">
                  {pushResult.sample.map((x) => `${x.sku}: ${x.from ?? '—'} → ${x.to}`).join(' · ')}
                  {pushResult.candidates > pushResult.sample.length && ' …'}
                </div>
              )}
            </div>
          )}
          <div className="text-[11px] font-bold uppercase tracking-wide text-[var(--text-muted)]">Recent pushes (button + automatic)</div>
          {pushLogs.length ? (
            <ul className="mt-1 space-y-1">
              {pushLogs.map((l) => (
                <li key={l.id} className="flex gap-2 text-xs">
                  <span className="shrink-0 text-[var(--text-muted)]">{new Date(l.createdAt).toLocaleString()}</span>
                  <span className={l.level === 'error' ? 'text-danger' : l.level === 'warn' ? 'text-warning' : 'text-[var(--text-primary)]'}>
                    {l.message.length > 160 ? l.message.slice(0, 160) + '…' : l.message}
                  </span>
                </li>
              ))}
            </ul>
          ) : (
            <p className="mt-1 text-xs text-[var(--text-muted)]">No pushes yet.</p>
          )}
        </div>

        {/* Catalog / SKU map */}
        <div className="rounded-card border border-[var(--border-color)] bg-[var(--card-bg)] p-4">
          <div className="mb-3 flex items-center justify-between">
            <div className="text-sm font-extrabold">Catalogue & SKU Map</div>
            <Button variant="glass" size="sm" icon={<RefreshCw size={13} />} onClick={() => sync.mutate()} disabled={sync.isPending}>{sync.isPending ? 'Syncing…' : 'Sync Catalog'}</Button>
          </div>
          <p className="mb-1 text-xs text-[var(--text-muted)]">Reads every Shopify variant (read-only) and maps it to an ERP item <strong>by barcode only</strong> (Shopify barcode = ERP barcode). SKU text and item code are not used. Fill barcodes in Shopify to map more.</p>
          {cfg?.lastCatalogSyncAt && (
            <p className="mb-3 text-xs text-[var(--text-muted)]">Last synced: {new Date(cfg.lastCatalogSyncAt).toLocaleString()}</p>
          )}
          {catalog && (
            <div className="grid grid-cols-3 gap-2 text-center">
              <Stat label="Variants" value={catalog.variants} />
              <Stat label="Mapped" value={catalog.mapped} tone="success" />
              <Stat label="By barcode" value={catalog.matchedByBarcode} tone={catalog.matchedByBarcode ? 'success' : 'muted'} />
              <Stat label="Unmapped" value={catalog.unmapped} tone={catalog.unmapped ? 'danger' : 'muted'} />
              <Stat label="Blank SKU" value={catalog.blankSku} tone={catalog.blankSku ? 'warning' : 'muted'} />
              <Stat label="Duplicates" value={catalog.duplicates} tone={catalog.duplicates ? 'warning' : 'muted'} />
              <Stat label="Untracked" value={catalog.untracked} tone={catalog.untracked ? 'warning' : 'muted'} />
            </div>
          )}
          <div className="mt-3 flex flex-wrap items-center gap-3 text-xs text-[var(--text-muted)]">
            <span>Mapped: <strong className="text-success">{skuQuery.data?.summary.mapped ?? 0}</strong></span>
            <span>Unmapped: <strong className="text-danger">{skuQuery.data?.summary.unmapped ?? 0}</strong></span>
            <span>Untracked: <strong className="text-warning">{skuQuery.data?.summary.untracked ?? 0}</strong></span>
            <Link to="/admin/configurations/shopify-mapping" className="ml-auto font-semibold text-accent hover:underline">
              Open SKU Mapping →
            </Link>
          </div>
        </div>
      </div>

    </div>
  );
}

function Stat({ label, value, tone = 'muted' }: { label: string; value: number; tone?: 'success' | 'danger' | 'warning' | 'muted' }) {
  const c = tone === 'success' ? 'text-success' : tone === 'danger' ? 'text-danger' : tone === 'warning' ? 'text-warning' : 'text-[var(--text-primary)]';
  return (
    <div className="rounded-md border border-[var(--border-color)] bg-[var(--bg-secondary)] p-2">
      <div className={`text-lg font-black ${c}`}>{value}</div>
      <div className="text-[10px] uppercase tracking-wide text-[var(--text-muted)]">{label}</div>
    </div>
  );
}
